import { useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import { useMutation } from "@apollo/client";

import { useAppState } from "core/contexts/AppContext";
import useToast from "core/hooks/useToast";
import { FieldList } from "core/hooks/useForm";
import { getCanRenderWebp } from "core/includes/image";
import { getShippingOptions, REMOVE_SHIPPING_FROM_ORDER } from "core/includes/queries";
import useSuggestedAddresses from "pages/Components/CartAndCheckout/hooks/useSuggestedAddresses";
import useCart from "pages/Components/CartPage/hooks/useCart";
import { getCountryCodeFromName } from "theme/utils";

type Props = {
    filteredFields: {
        fields: FieldList,
        billingFields: FieldList,
        contactFields: FieldList,
        readOnlyFields: string[]
    },
    usedAddressFinder: boolean
}

const useShipping = ({ filteredFields, usedAddressFinder = false }: Props) => {
    const [, errorToast] = useToast();
    const [appState, setAppState] = useAppState();

    const suggestedAddresses = useSuggestedAddresses();
    const { processCart, getLocalStorageCartUuid } = useCart();

    const [loadShippingOptions, { loading: isShippingLoading }] = useLazyQuery(getShippingOptions);
    const [removeShippingFromOrder] = useMutation(REMOVE_SHIPPING_FROM_ORDER);

    /**
     * Gets shipping option based on the passed parameters of the product. These dimensions should be set in the CMS.
     * NZPostAPI is used for NZ customers, TransDirect API is used for all customers.
     *
     * When we get all the appropriate shipping options for an item, we filter out by the cheapest and that require
     * signature add-on. Since an add-one has its own price, we have to add it separately to the cost. If such shipping
     * exists, then we add it to the cart.
     *
     * If no shipping is found, then a user cannot check out :P
     *
     * @param {number} parcelWeight
     * @param {number} parcelWidth
     * @param {number} parcelHeight
     * @param {number} parcelDepth
     *
     * @return {Promise}
     */
    const getShipping = useCallback(async () => {
        let shippingOptions: any = {};
        let suburbOptions: string[] = [];

        if (!appState.cart) {
            return { shippingOptions, suburbOptions };
        }

        // If the street number was not provided from the address search, then the address needs to be updates.
        // Global addresses may not include a street number.
        // Show a toast error message to double-check the address
        if (usedAddressFinder && !appState.globalCurrencyOptions.isGlobal && !filteredFields.contactFields.streetNumber) {
            errorToast("Please check your address.");
            return { shippingOptions, suburbOptions };
        }

        await loadShippingOptions({
            variables: {
                orderUuid: appState.cart.orderUuid,
                orderCountryCode: appState.cart.countryCode,
                dropOffAddress: filteredFields.contactFields.addressOne + ',' + filteredFields.contactFields.addressTwo,
                dropOffPostcode: filteredFields.contactFields.postCode,
                dropOffState: filteredFields.contactFields.state,
                dropOffStateCode: filteredFields.contactFields.stateShort,
                dropOffSuburb: filteredFields.contactFields.city,
                dropOffCountryCode: getCountryCodeFromName(filteredFields.contactFields.country),
                dutiableAmount: appState.cart?.subtotalAmount ?? 0, // for international orders
                returnAllQuotes: true,
                canRenderWebp: getCanRenderWebp()
            },
        }).then((res: any) => {
            if (res.error?.graphQLErrors.length) {
                throw new Error(res.error.graphQLErrors[0].debugMessage);
            }

            shippingOptions = res.data?.shippingOptions;
            if (shippingOptions?.shippingAmount > 0) {
                setAppState((prevState: any) => ({
                    cart: {
                        ...prevState.cart,
                        shippingAmount: shippingOptions.shippingAmount,
                    }
                }));

                processCart(shippingOptions);
            }
        }).catch(() => {
            throw new Error("Error has occurred while verifying the address.  Please contact us.");
        });

        return { shippingOptions, suburbOptions };
    }, [
        appState.cart,
        usedAddressFinder,
        filteredFields.contactFields.streetNumber,
        filteredFields.contactFields.addressOne,
        filteredFields.contactFields.addressTwo,
        filteredFields.contactFields.postCode,
        filteredFields.contactFields.state,
        filteredFields.contactFields.stateShort,
        filteredFields.contactFields.city,
        filteredFields.contactFields.country,
        loadShippingOptions,
        errorToast,
        setAppState,
        processCart,
        suggestedAddresses
    ]);

    /**
     * Goes over all available package boxes and tries to pack the products into them. Based on this we find the most
     * appropriate package for the user to use. This will be used to identify the price of shipping later.
     *
     * @return {Promise}
     */
    const packageProducts = useCallback(async () => {
        let shippingOptions: any = {};

        if (!appState.cart) return shippingOptions;

        await getShipping()
            .then((res) => shippingOptions = res)
            .catch((err) => {
                throw err;
            });

        return shippingOptions;
    }, [appState.cart, errorToast, getShipping]);

    /**
     * Removes the shipping from the order. Mainly for when a customer navigates away. The updated cart is returned.
     *
     * @return {Cart} cart
     */
    const removeShipping = useCallback(() => {
        removeShippingFromOrder({
            variables: {
                orderUuid: appState.cart?.orderUuid || getLocalStorageCartUuid(),
                canRenderWebp: getCanRenderWebp()
            }
        }).then(res => {
            const cart = res.data.removeShippingFromOrder;
            processCart(cart);
            return cart;
        }).catch(() => {
            errorToast('Error has occurred. Please contact us.');
        });
    }, [appState.cart?.orderUuid, errorToast, getLocalStorageCartUuid, processCart, removeShippingFromOrder]);

    return {
        packageProducts,
        removeShipping,
        isShippingLoading
    }
}

export default useShipping;
